import type {Vector2} from "../delme_globals";
import {GreyOutState, OwayoKonfiPart} from "../owayoPartWithSelObs";
import {THREE} from "../resource/three";
import type {PartSelectableObject} from "../partSelOb";
import {owayoVertexShader, ShaderCreator} from "./OwayoShader";
import {OwayoPartBaseClass} from "../../../vorschauviewer/src/js/owayoPartBaseClass";
import {curry} from "@k/global/functionalTools";
import {PartForMaterial} from "../owayoJSViewerWithSelObs";
import {SelObShaderUniformCreator} from "@k/js/shader/SelObShaderUniformCreator";

export function getStandardLightShader(offsetRepeat:Vector2, isGreyedOut: GreyOutState = GreyOutState.NOT_GREYED_OUT) {
    return THREE.UniformsUtils.merge([
        THREE.UniformsLib.lights,
        {
            "diffuse": {type: "c", value: new THREE.Color(0xeeeeee)},
            "ambient": {type: "c", value: new THREE.Color(0xffffff)},
            "emissive": {type: "c", value: new THREE.Color(0x000000)},
            "offsetRepeat": {type: "v4", value: offsetRepeat},
            "isGreyedOut": {type: "i", value: (isGreyedOut === GreyOutState.GREYED_OUT ? 1 : 0)},
            "isGreyedOutExceptDruckbereich": {type:"i", value:(isGreyedOut === GreyOutState.OUT_OF_DRUCKBEREICH_GREYED_OUT ? 1 : 0)}
        }
    ]);
}

export function getShaderUniformsForSingleShader(part:OwayoKonfiPart|OwayoPartBaseClass, withColors:boolean = false, patternsAndSelObs:PartSelectableObject[] = [], anzSelObsToDisplay:number = 0) {
    let ShaderUniforms = getStandardLightShader(part.offsetRepeat, part.isGreyedOut);
    ShaderUniforms = addMaterialUniformsIfNeeded(part, ShaderUniforms);
    if (withColors) {
        ShaderUniforms = THREE.UniformsUtils.merge([
            ShaderUniforms,
            { "ColorTextures": {type: "tv", value: [(part as OwayoKonfiPart).colorTexture, part.parent.colorValueTexture]}}
        ]);
    }
    if(part.overlayMap) {
        ShaderUniforms = addOverlayMapUniforms(ShaderUniforms, part);
    }

    if(anzSelObsToDisplay === 0) {
        return ShaderUniforms;
    }
    return getShaderUniformsForSelObsAndPatterns(ShaderUniforms, patternsAndSelObs, anzSelObsToDisplay);
}

export function addOverlayMapUniforms(currentShaderUniforms:any, part:OwayoPartBaseClass) {
    const matrix = new THREE.Matrix3().set((1/(part.overlayScale.x * part.partStauchungsFaktor))/0.5, 0, 0, 0, (1/part.overlayScale.y)/0.5, 0, 0, 0,1);
    return  THREE.UniformsUtils.merge([
        currentShaderUniforms,
        {
            "overlayMap":{type:"tv", value:[part.overlayMap]},
            "overlayScalingMatrix":{type:"m3", value:matrix},
            "overlayMapSize":{type:"v2", value:new THREE.Vector2((part.overlayMap as THREE.Texture).image.width,(part.overlayMap as THREE.Texture).image.height)},
        }
    ])
}

export function getShaderUniformsForSelObsAndPatterns(currentUniforms:any, patternsAndSelObs:PartSelectableObject[], anzSelObsToDisplay:number){
    const creator = new SelObShaderUniformCreator();

    for (let i = 0; i < anzSelObsToDisplay; i++) {
        creator.addSelOb(patternsAndSelObs[i]);
    }

    if(!creator.isEmpty()) {
        currentUniforms = THREE.UniformsUtils.merge([
            currentUniforms,
            creator.getShaderUniformObject()
        ]);
    }
    return currentUniforms;
}

export function getShaderMaterial(ShaderUniforms:any, FragmentShader:string, alphaMap:{image:HTMLImageElement}, withColors:boolean):THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
        vertexShader: owayoVertexShader.join('\n'),
        uniforms: ShaderUniforms,
        fragmentShader: FragmentShader,
        transparent: !!(alphaMap && alphaMap.image) || !withColors,
        lights: true,
        shading: THREE.SmoothShading,
        side: THREE.DoubleSide
    });
}

export function addMaterialUniformsIfNeeded(part: PartForMaterial, ShaderUniforms:any) {

    const mapAndImagePresent = curry(ShaderCreator.mapAndImagePresent, part);
    const addUniforms = (o:any)=>THREE.UniformsUtils.merge([ShaderUniforms, o]);

    if (mapAndImagePresent("alphaMap")) {
        ShaderUniforms = addUniforms({"alphaMap": {type: "tv", value: [part.alphaMap]}});
    }
    if (mapAndImagePresent("bumpMap")) {
        ShaderUniforms = addUniforms({
                "bumpMap": {type: "tv", value: [part.bumpMap]},
                "bumpScale": {type: "f", value: part.bumpScale}
            }
        );
    }
    if (mapAndImagePresent("normalMap")) {
        ShaderUniforms = addUniforms({
                "normalMap": {type: "tv", value: [part.normalMap]},
                "normalScale": {type: "v2", value: new THREE.Vector2(part.normalScale)}
            }
        );
    }
    if (mapAndImagePresent("specularMap")) {
        ShaderUniforms = addUniforms({"specularMap": {type: "tv", value: [part.specularMap]}});
    }

    if (mapAndImagePresent("shadowMap")) {
        ShaderUniforms = addUniforms({"shadowMap": {type: "tv", value: [part.shadowMap]}});
    }

    if(mapAndImagePresent("overlayMap")) {
        ShaderUniforms = addUniforms({"overlayMap": {type:"tv", value:[part.overlayMap]}})
    }

    return ShaderUniforms;
}
